import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Loader2, PackagePlus, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useCOAProcessing, type COAOutcome } from '@/contexts/COAProcessingContext';
import { useFeatureRegistration, type FeatureMeta } from '@/contexts/FeatureRegistryContext';
import { generateReferenceId } from '@/lib/referenceId';
import { cn } from '@/lib/utils';
import { isCoaTerminal, saveCardOutcome } from '@/lib/chatCards';
import type { DraftProposal } from './streamChat';

export const FEATURE_META: FeatureMeta = {
  id: 'chat-listing-draft',
  label: 'Create a listing from chat',
  description:
    'Tell Ask Cannafax about a product (typed, spoken or with a COA attached) and a card lets you create it as an active listing right in the chat.',
  triggerType: 'action',
  personas: ['seller', 'buyer_seller'],
};

/** One-line summary of what the draft will contain, in plain words. */
export function draftSummary(p: DraftProposal): string {
  const parts = [p.strainName];
  if (p.productForm) parts.push(p.productForm);
  if (p.quantityValue != null) parts.push(`${p.quantityValue} ${p.quantityUnit ?? ''}`.trim());
  if (p.priceValue != null) parts.push(`$${p.priceValue}/${p.priceUnit ?? 'lb'}`);
  if (p.thcPercent != null) parts.push(`THC ${p.thcPercent}%`);
  return parts.join(' · ');
}

type State = 'offer' | 'creating' | 'analyzing' | 'review' | 'done' | 'failed' | 'dismissed';
type LabNote = 'none' | 'applied' | 'saved' | 'unread' | 'kept';

/** Remembers which draft a card created, so coming back to the chat never offers a duplicate. */
const memoKey = (p: DraftProposal) =>
  `chat-draft:${[p.strainName, p.productForm, p.quantityValue, p.priceValue, p.coaFileName].join('|')}`;
export function rememberedDraft(p: DraftProposal): string | null {
  try { return localStorage.getItem(memoKey(p)); } catch { return null; }
}

export function ChatDraftCard({ proposal, coaFile, threadId }: { proposal: DraftProposal; coaFile?: File; threadId?: string | null }) {
  useFeatureRegistration(FEATURE_META);
  const { user } = useAuth();
  const coa = useCOAProcessing();
  const saved = proposal.outcome;
  const remembered = saved?.listingId ?? rememberedDraft(proposal);
  const [listingId, setListingId] = useState<string | null>(remembered);
  const [state, setState] = useState<State>(() => {
    if (saved && ['done', 'dismissed', 'failed'].includes(saved.state)) return saved.state as State;
    if (!remembered) return 'offer';
    if (coa.getPendingConfirm(remembered)) return 'review';
    // Created earlier: only the real lab-report result (or none to wait for) can say it's ready.
    return proposal.coaFileName || coa.isProcessing(remembered) ? 'analyzing' : 'done';
  });
  const [error, setError] = useState<string | null>(saved?.error ?? null);
  const [lab, setLab] = useState<LabNote>((saved?.lab as LabNote) ?? 'none');

  const processing = listingId ? coa.isProcessing(listingId) : false;
  const pending = listingId ? coa.getPendingConfirm(listingId) : null;
  const result = listingId ? coa.getResultForListing(listingId) : null;

  // Keep the conversation's record of this card current, so reopening the chat shows it as it is.
  useEffect(() => {
    if (state === 'offer' || state === 'creating') return;
    void saveCardOutcome(threadId, proposal.cardId, { state, listingId, lab, error });
  }, [state, listingId, lab, error, threadId, proposal.cardId]);

  const labFromStatus = (status: string): LabNote =>
    status === 'completed' ? 'applied' : status === 'not_a_coa' ? 'saved' : 'unread';

  // Follow the lab report to its real end before saying the draft is ready. Never infer
  // "finished" from the absence of a local job — ask the backend's own job record instead.
  useEffect(() => {
    if (state !== 'analyzing' || !listingId) return;
    if (pending) { setState('review'); return; }
    if (processing) return;
    if (result) { setLab(labFromStatus(result.status)); setState('done'); return; }
    let stopped = false;
    let timer: ReturnType<typeof setTimeout>;
    const check = async () => {
      const { data } = await supabase
        .from('coa_jobs')
        .select('status')
        .eq('listing_id', listingId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (stopped) return;
      if (data && isCoaTerminal(data.status)) { setLab(labFromStatus(data.status)); setState('done'); return; }
      timer = setTimeout(check, 5000);
    };
    void check();
    return () => { stopped = true; clearTimeout(timer); };
  }, [state, listingId, pending, processing, result]);

  const finishCoa = (outcome: COAOutcome) => {
    if (outcome === 'queued') setState('analyzing');
    else if (outcome === 'review') setState('review');
    else { setLab(outcome === 'applied' ? 'applied' : outcome === 'saved' ? 'saved' : 'unread'); setState('done'); }
  };

  const create = async () => {
    if (!user) return;
    setState('creating');
    setError(null);
    try {
      const { data: me } = await supabase.from('users').select('company_id').eq('id', user.id).single();
      const companyId = me?.company_id;
      if (!companyId) throw new Error('Your account is not linked to a company yet.');
      const { data: co } = await supabase.from('companies').select('preferred_currency').eq('id', companyId).maybeSingle();
      const id = listingId ?? crypto.randomUUID();
      if (!listingId) {
        const { error: insErr } = await supabase.from('listings').insert({
          id,
          company_id: companyId,
          status: 'active',
          reference_id: generateReferenceId(),
          creation_source: 'manual',
          created_by_user_id: user.id,
          currency: co?.preferred_currency || 'USD',
          dimension: proposal.quantityUnit === 'unit' ? 'count' : 'mass',
          strain_name: proposal.strainName,
          product_form: proposal.productForm,
          species: proposal.species,
          quantity_value: proposal.quantityValue,
          quantity_unit: proposal.quantityUnit ?? 'lb',
          price_value: proposal.priceValue,
          price_unit: proposal.priceUnit ?? 'lb',
          thc_percent: proposal.thcPercent,
          cbd_percent: proposal.cbdPercent,
        });
        if (insErr) throw new Error('The listing could not be created. Please try again.');
        setListingId(id);
        try { localStorage.setItem(memoKey(proposal), id); } catch { /* private mode */ }
      }

      if (!coaFile) { setState('done'); toast.success('Listing created', { description: proposal.strainName }); return; }

      const path = `${id}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.pdf`;
      const { error: upErr } = await supabase.storage.from('listing_documents').upload(path, coaFile);
      if (upErr) { setLab('unread'); setState('done'); return; }
      const { error: docErr } = await supabase.from('listings').update({
        documents: [path],
        document_meta: { [path]: { original_name: coaFile.name, uploaded_at: new Date().toISOString() } },
      }).eq('id', id);
      if (docErr) { setLab('unread'); setState('done'); return; }
      // Stay in "creating" until the lab-report service has actually accepted the job.
      // Same COA pipeline as the listing editor; resolves with what really happened.
      finishCoa(await coa.startProcessing({ listingId: id, documentPath: path, companyId, listingStrain: proposal.strainName }));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Please try again.');
      setState('failed');
    }
  };

  const applyAnyway = async () => {
    if (!listingId) return;
    setState('creating');
    finishCoa(await coa.confirmForce(listingId));
  };
  const keepDocOnly = () => {
    if (!listingId) return;
    coa.declineForce(listingId);
    setLab('kept');
    setState('done');
  };

  const fileGone = state === 'offer' && !!proposal.coaFileName && !coaFile;

  return (
    <div
      className={cn('mt-2 w-full rounded-xl border border-border bg-card p-4 text-sm shadow-sm', state === 'dismissed' && 'opacity-60')}
      aria-disabled={state === 'dismissed' || undefined}
    >
      <div className="flex items-start gap-3">
        <PackagePlus className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1">
          <p className="font-medium text-foreground">{draftSummary(proposal)}</p>
          {proposal.coaFileName && <p className="truncate text-muted-foreground">Lab report: {proposal.coaFileName}</p>}
        </div>
      </div>

      {state === 'offer' && (
        <>
          {proposal.missing.length > 0 && (
            <p className="mt-3 text-xs text-muted-foreground">
              Still missing: {proposal.missing.join(', ')}. You can add these on the listing later.
            </p>
          )}
          <p className="mt-3 text-xs text-muted-foreground">
            {fileGone
              ? 'The lab report is no longer attached. Attach it again and ask me to create this listing.'
              : "It goes straight into your active listings."}
          </p>
          <div className="mt-3 flex items-center gap-2">
            <Button size="sm" onClick={create} disabled={fileGone}>Create listing</Button>
            <Button size="sm" variant="quiet" onClick={() => setState('dismissed')}>Not now</Button>
          </div>
        </>
      )}

      {(state === 'creating' || state === 'analyzing') && (
        <div className="mt-3 space-y-1">
          <p className="flex items-center gap-2 text-muted-foreground" role="status">
            <Loader2 className="h-4 w-4 animate-spin" />
            {state === 'creating'
              ? (listingId && coaFile ? 'Sending the lab report for reading…' : coaFile ? 'Creating listing and attaching lab report…' : 'Creating listing…')
              : (listingId && coa.getStatusMessage(listingId)) || 'Reading the lab report…'}
          </p>
          {state === 'analyzing' && (
            <p className="text-xs text-muted-foreground">
              Reading a lab report usually takes a few minutes. You can keep working or leave — you'll get an in-app notice when it's done, and this card picks up where it left off. The listing opens once it's ready.
            </p>
          )}
        </div>
      )}

      {state === 'review' && pending && (
        <div className="mt-3 space-y-2">
          <p className="flex items-start gap-2 font-medium text-foreground">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-destructive" />
            {pending.reason === 'mismatch'
              ? `The lab report looks like it's for “${pending.detectedStrain || 'another strain'}”, not “${proposal.strainName}”.`
              : pending.reason === 'unreadable'
                ? "This PDF has no readable text (it may be a scan), so we couldn't check the strain."
                : `We couldn't confirm the lab report is for “${proposal.strainName}”.`}
          </p>
          <p className="text-xs text-muted-foreground">The report stays on the listing either way. Applying lab data never changes the strain name.</p>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={applyAnyway}>Apply lab data anyway</Button>
            <Button size="sm" variant="quiet" onClick={keepDocOnly}>Keep report only</Button>
          </div>
        </div>
      )}

      {state === 'done' && listingId && (
        <div className="mt-3 space-y-2">
          <p className="flex items-center gap-2 font-medium text-foreground">
            <CheckCircle2 className="h-4 w-4 text-primary" />
            Listing created and active
          </p>
          {lab === 'applied' && <p className="text-xs text-muted-foreground">Lab results from the report were added to the listing.</p>}
          {lab === 'saved' && <p className="text-xs text-muted-foreground">The file is on the listing, but it didn't look like a lab report, so no results were added.</p>}
          {lab === 'kept' && <p className="text-xs text-muted-foreground">The lab report is on the listing; its results were not applied.</p>}
          {lab === 'unread' && <p className="text-xs text-muted-foreground">We couldn't read the lab report. You can add or retry it on the listing.</p>}
          <Button size="sm" asChild>
            <Link to={`/listings/${listingId}/edit`}>Open listing</Link>
          </Button>
        </div>
      )}

      {state === 'dismissed' && (
        <p className="mt-3 text-xs text-muted-foreground">Not created — you chose not now.</p>
      )}

      {state === 'failed' && (
        <div className="mt-3 space-y-2">
          <p className="flex items-start gap-2 text-destructive">
            <XCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            {error}
          </p>
          <Button size="sm" variant="quiet" onClick={create}>Try again</Button>
        </div>
      )}
    </div>
  );
}
